import { BaseService } from '@/modules/core/app/services/abstracts/BaseService';
import { ReportStudioElement } from '@/modules/ta/reportstudio/models/ReportStudioElement';
import Logger from '@/modules/core/app/helpers/Logger';

const CLIPBOARD_KEY = 'reportstudio-element-clipboard';
const CLIPBOARD_TTL_MS = 60 * 60 * 1000;

export class ReportStudioElementClipboardService extends BaseService {
  /**
   * Persist one or more copied canvas elements to localStorage so they can be pasted into
   * another report, including from a different browser tab on the same origin.
   *
   * @param {{ sourceReportId: string, elements: ReportStudioElement[] }} payload
   * @returns {boolean} false if the write failed (e.g. localStorage quota exceeded on a
   *   large multi-select), so callers can surface the failure instead of silently no-oping.
   */
  copyElements({ sourceReportId, elements }) {
    const clipboard = {
      source_report_id: sourceReportId,
      elements,
      copied_at: Date.now(),
    };
    try {
      window.localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(clipboard));
      return true;
    } catch (e) {
      Logger.log(e);
      return false;
    }
  }

  /**
   * @returns {ReportStudioElement[]}
   */
  getElements() {
    try {
      const raw = window.localStorage.getItem(CLIPBOARD_KEY);
      if (!raw) {
        return [];
      }
      const clipboard = JSON.parse(raw);
      if (Date.now() - (clipboard?.copied_at ?? 0) > CLIPBOARD_TTL_MS) {
        this.clear();
        return [];
      }
      const elements = Array.isArray(clipboard?.elements) ? clipboard.elements : [];
      return elements
        .filter((element) => element?.id)
        .map((element) => new ReportStudioElement(element));
    } catch (e) {
      Logger.log(e);
      return [];
    }
  }

  hasElements() {
    return this.getElements().length > 0;
  }

  clear() {
    window.localStorage.removeItem(CLIPBOARD_KEY);
  }
}

export default new ReportStudioElementClipboardService();
